import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
// import { useAuthenticator } from '@aws-amplify/ui-react';
import Layout from '../components/Layout';
import { addUserToTrialGroup, checkUserGroups } from '../utils/cognitoGroups';
import '../styles/homepage-quantumblack.css';
import predictiveAnalyticsVideo from '../assets/predictive.mp4';
import chatBotVideo from '../assets/chatbot.mp4';
import aiImage from '../assets/ai-image.jpg';
import dataPreparationImage from '../assets/data-preparation.jpg';
import predictiveAnalyticsImage from '../assets/predictive-analytics.jpg';
import computerVisionImage from '../assets/computer-vision.jpg';
import nlpImage from '../assets/nlp.jpg';
import recommendationSystemImage from '../assets/recommendation-system.jpg';
import secureHealthcareImage from '../assets/secureHealthcare.jpg';
import chatBotImage from '../assets/Chatbot.jpg';
import commandCenterImage from '../assets/CommandCenter-small.jpeg';


const services = [
  {
    title: "Agent Atlas",
    description: "Govern agentic AI. Declares what every agent is, what it's allowed to do, and proves it before it acts, so autonomy never outruns oversight.",
    image: aiImage,
  },
  {
    title: "Almanac", 
    description: "Automate and govern the ML lifecycle. Every consequential step — promote a model, deploy it, act on it — clears a formal proof before it takes effect, with an audit trail that doubles as compliance evidence.",
    image: predictiveAnalyticsImage,
  },
  {
    title: "Noesis",
    description: "The flagship. Governed clinical AI for documentation and revenue cycle, proving the approach under the hardest constraints: regulated data, fully on-premises, every inference traceable to its source.",
    image: secureHealthcareImage,
  },
];

const HomePage = ({ user }) => {
  const navigate = useNavigate();
  // Use the user prop passed from App instead of useAuthenticator hook
  const authUser = user;
  

  return (
    <Layout>
      <div className="container homepage">
        <div className="page-heading">
          <img 
            className="header-background"
            src={commandCenterImage}
            alt="Fox River AI Command Center"
          />
          <div className="header-overlay"></div>
          <div className="header-content">
            <h1>AI with formal proofs, not plausible guesses.</h1>
            <div className="intro">
              <div className="intro-column">
                <p>Fox River AI builds the governance and lifecycle tooling that lets you put autonomous AI behind consequential decisions — across agentic systems and ML pipelines — where every action is proven before it fires and runs where your data lives.</p>
              </div>
              <div className="intro-column">
                <p>The market is filling with 'guardrails' that are themselves language models — a plausible guess checking a plausible guess. We build the opposite: a formal gate proves each consequential step against your declared rules before it executes, and every decision leaves an audit trail you can hand to a regulator. We prove it in the most demanding environment there is — regulated clinical work, fully on-premises — with Noesis, our most complete system.</p>
              </div>
            </div>
          </div>
        </div>

        <section className="services">
          <h2 className="section-heading">Our Solutions</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={index}>
                <div className="service-media">
                  {service.video ? (
                    <>
                      <video 
                        className="service-video" 
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        preload="auto"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error('Video error:', e.target.error);
                          e.target.style.display = 'none';
                          const fallbackImg = e.target.parentElement?.querySelector('img');
                          if (fallbackImg) {
                            fallbackImg.style.display = 'block';
                          }
                        }}
                        onLoadStart={() => console.log('Video loading started')}
                        onCanPlay={() => console.log('Video can play')}
                      >
                        <source src={service.video} type="video/mp4; codecs=avc1.42E01E,mp4a.40.2" />
                        Your browser does not support the video tag.
                      </video>
                      <img 
                        src={service.image} 
                        alt={service.title} 
                        style={{display: 'none'}}
                      />
                    </>
                  ) : (
                    <img src={service.image} alt={service.title} />
                  )}
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link 
                    to="/contact-us"
                    className="cta-button"
                    onClick={() => {
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 100);
                    }}
                  >
                    Contact us
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="security">
          <div className="security-content-wrapper">
            <div className="security-content">
              <h2>Enterprise-Grade Security & Privacy</h2>
              <p>At Fox River AI, safeguarding your data is our top priority. We implement strong data protection practices — including encryption, strict access controls, and privacy-first design — to ensure your business information remains secure and confidential.</p>
              <p>Whether you're sharing customer records, operational data, or internal documents, your information is protected every step of the way. We follow industry best practices and comply with standards like GDPR and HIPAA, so you can innovate with confidence, knowing your data is in trusted hands.</p>
            </div>
            <div className="security-media">
              <img src={secureHealthcareImage} alt="Security and Privacy" />
            </div>
          </div>
        </section>      

        <section className="why-choose-us">
          <h2 className="section-heading">Provable, not plausible</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>Formal verification</h3>
              <p>Every consequential action is proven against your declared rules before it runs — a formal check, not a language model second-guessing another language model. When it refuses, it tells you which rule and why.</p>
            </div>
            <div className="benefit-item">
              <h3>Runs where you need it</h3>
              <p>Deploy in your cloud, on your own servers, or fully on-premises where your data never leaves your network — including the regulated, locked-down environments most AI vendors can't serve at all. The deployment bends to your constraints, not the other way around.</p>
            </div>
            <div className="benefit-item">
              <h3>Audit-ready compliance</h3>
              <p>Every decision leaves an immutable, replayable record that maps to frameworks like the NIST AI RMF. Governance stops being a document you write afterward and becomes a byproduct of how the system runs.</p>
            </div>
            <div className="benefit-item">
              <h3>Built by enterprise hands</h3>
              <p>Thirty years architecting systems inside regulated enterprises — financial services, defense, insurance, healthcare. You're not paying a startup to learn compliance on your project.</p>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;